import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import FilterPanel from "../../components/FilterPanel";
import Input from "../../components/Input";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import Pagination from "../../components/Pagination";
import Select from "../../components/Select";
import Table from "../../components/Table";
import UserCell from "../../components/UserCell";
import { api } from "../../services/api";

const SubjectCell = ({ subject }) =>
  subject ? (
    <div>
      <div className="font-medium">{subject.name}</div>
      <div className="text-xs text-gray-500">{subject.code}</div>
    </div>
  ) : (
    <span className="italic text-gray-400">N/A</span>
  );

const StatusCell = ({ status, variant }) => (
  <Badge
    label={status?.replace("_", " ")}
    variant={variant}
    size="small"
    className="capitalize"
  />
);

export default function AdminSessions() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await api.get("/admin/system/settings");
        setSettings(response.settings);
      } catch (error) {
        console.error("Failed to load settings:", error);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [statusFilter, page, searchQuery, settings]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: 50,
        page,
        ...(statusFilter && { status: statusFilter }),
      });

      const response = await api.get(`/admin/sessions?${params}`, {
        withCredentials: true,
      });

      if (response?.success) {
        let filtered = response.sessions || [];

        // Filter out scheduled sessions if setting is enabled
        if (settings?.hideScheduledSessions) {
          filtered = filtered.filter((s) => s.status !== "scheduled");
        }

        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          filtered = filtered.filter((s) => {
            const sName = `${s.studentId?.name?.first || ""} ${
              s.studentId?.name?.last || ""
            }`.toLowerCase();
            const tName = `${s.tutorId?.name?.first || ""} ${
              s.tutorId?.name?.last || ""
            }`.toLowerCase();
            const email = `${s.studentId?.email || ""} ${
              s.tutorId?.email || ""
            }`.toLowerCase();
            return sName.includes(q) || tName.includes(q) || email.includes(q);
          });
        }

        setSessions(filtered);
        setPagination(response.pagination || {});
      } else {
        setError("Invalid response from server");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to fetch sessions"
      );
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, searchQuery, settings]);

  const getStatusVariant = (status) =>
    ({
      scheduled: "info",
      ongoing: "warning",
      completed: "success",
      cancelled: "danger",
      no_show: "danger",
    }[status] || "default");

  const statusVariantMap = {
    scheduled: "info",
    ongoing: "warning",
    completed: "success",
    cancelled: "danger",
    no_show: "danger",
  };

  const columns = [
    {
      header: "Student",
      sortable: false,
      render: (row) => <UserCell user={row.studentId} />,
    },
    {
      header: "Tutor",
      sortable: false,
      render: (row) => <UserCell user={row.tutorId} />,
    },
    {
      header: "Subject",
      sortable: false,
      render: (row) => <SubjectCell subject={row.subjectId} />,
    },
    {
      header: "Date & Time",
      accessor: "startAt",
      sortable: true,
      render: (row) =>
        row.startAt ? dayjs(row.startAt).format("MMM DD, YYYY HH:mm") : "N/A",
    },
    {
      header: "Duration",
      accessor: "duration",
      sortable: true,
      render: (row) => (row.duration ? `${row.duration} min` : "N/A"),
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      render: (row) => (
        <StatusCell
          status={row.status}
          variant={statusVariantMap[row.status]}
        />
      ),
    },
    {
      header: "Action",
      sortable: false,
      render: (row) =>
        row._id ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/sessions/${row._id}`)}
          >
            View
          </Button>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "scheduled", label: "Scheduled" },
    { value: "ongoing", label: "Ongoing" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "no_show", label: "No Show" },
  ];

  const hasFilters = statusFilter || searchQuery;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <PageHeader
        title="All Sessions"
        subtitle="View and manage all tutoring sessions"
      />

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-md flex justify-between items-center">
          <span>{error}</span>
          <Button variant="danger" size="sm" onClick={() => fetchSessions()}>
            Retry
          </Button>
        </div>
      )}

      <FilterPanel title="Filters">
        <Input
          label="Search by Name or Email"
          placeholder="Search sessions..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
        />
        <Select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          options={statusOptions}
        />
      </FilterPanel>

      {hasFilters && (
        <div className="mb-4 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setStatusFilter("");
              setSearchQuery("");
              setPage(1);
            }}
          >
            Clear Filters
          </Button>
          <span className="text-sm text-text opacity-60 py-2">
            {sessions.length} sessions found
          </span>
        </div>
      )}

      <Table
        columns={columns}
        data={sessions}
        loading={loading}
        emptyMessage={
          hasFilters ? "No sessions match your filters" : "No sessions found"
        }
      />

      {pagination.pages > 1 && (
        <div className="mt-6 flex flex-col gap-4">
          <p className="text-sm text-text">
            Showing {sessions.length} of {pagination.total} sessions
          </p>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
